import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  VariantUpdateEvent,
  QuantitySelectorUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    this.animateAddToCart();

    const animationEnabled = false; // this.dataset.addToCartAnimation === 'true';

    if (animationEnabled && !event.target.closest('.quick-add-modal')) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;

    // Try to find the cart-drawer-component first
    const cartDrawer = document.querySelector('cart-drawer-component');
    let destination = null;

    if (cartDrawer) {
      // If drawer is open, target the dialog, otherwise target the drawer component itself
      const dialog = /** @type {HTMLDialogElement | null} */ (cartDrawer.querySelector('.cart-drawer__dialog'));
      if (dialog && dialog.open) {
        destination = dialog;
      } else {
        destination = cartDrawer;
      }
    }

    // Fallback to cart icon if no drawer found
    if (!destination) {
      destination = document.querySelector('.header-actions__cart-icon');
    }

    const image = this.dataset.productVariantMedia;

    if (!destination || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = destination;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 * @property {HTMLElement | undefined} stockStatusMessage - The stock status message element.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean | undefined} */
  #variantAvailable;

  /** @type {number | undefined} */
  #cartMonitorInterval;

  /** @type {string} */
  #lastCartHash = '';

  /** @type {((cart: any) => void) | undefined} */
  #cartMonitorCallback;

  // Removed #pendingCustomColorFee - no longer needed since custom color fee is handled as line item properties

  // Static shared cart cache and request deduplication
  /** @type {{ cart: any; timestamp: number } | null} */
  static #cartCache = null;
  static #CART_CACHE_TTL = 2000; // 2 seconds cache TTL
  /** @type {Promise<any> | null} */
  static #pendingCartRequest = null;
  /** @type {number | undefined} */
  static #sharedCartMonitorInterval;
  static #cartMonitorCallbacks = new Set();
  static #initialized = false;

  /**
   * Initialize static cart cache invalidation listeners.
   */
  static #initialize() {
    if (ProductFormComponent.#initialized) return;
    ProductFormComponent.#initialized = true;

    // Invalidate cache on cart update events
    document.addEventListener(ThemeEvents.cartUpdate, () => {
      ProductFormComponent.#invalidateCartCache();
    });
  }

  connectedCallback() {
    super.connectedCallback();

    // Initialize static cart cache system
    ProductFormComponent.#initialize();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    target?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });

    this.addEventListener('change', this.#onInputChange, { signal });
    this.addEventListener('input', this.#onInputChange, { signal });

    // Initialize add to cart button price
    this.#initializeVariantPrice();
    this.updateAddToCartButtonPrice();

    const currentVariantId = this.refs.variantId?.value;
    if (currentVariantId) {
      this.#readMetafieldsFromDOM(currentVariantId);
    }

    this.#initializeSkuFromVariant();

    // Start cart monitor for warranty quantity sync
    this.#startCartMonitor();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    if (this.#cartMonitorInterval) {
      clearInterval(this.#cartMonitorInterval);
      this.#cartMonitorInterval = undefined;
    }
    // Remove this component's callback from the shared monitor
    if (this.#cartMonitorCallback) {
      ProductFormComponent.#cartMonitorCallbacks.delete(this.#cartMonitorCallback);
      this.#cartMonitorCallback = undefined;
    }
  }

  /**
   * Gets selected add-on variants from the related products dropdown.
   * Returns variant ID and SKU (when available) for each selected card.
   *
   * @returns {{ id: string; sku: string }[]} Array of { id, sku } for selected add-ons
   */
  #getSelectedAddOnVariants() {
    const selectedCards = document.querySelectorAll('.variant-card.selected');
    return Array.from(selectedCards)
      .map((card) => {
        const el = /** @type {HTMLElement} */ (card);
        const id = el.dataset.variantId;
        return id ? { id, sku: (el.dataset.variantSku || '').trim() } : null;
      })
      .filter((v) => v != null);
  }

  /**
   * Gets custom color option data if provided.
   *
   * @returns {{colorCode: string, price: number} | null} Custom color data or null
   */
  #getCustomColorData() {
    const colorInput = /** @type {HTMLInputElement | null} */ (document.getElementById('custom-color-code'));
    if (!colorInput || !colorInput.value.trim()) {
      return null;
    }

    const colorCode = colorInput.value.trim();
    // Read from the product-form-component dataset which is in cents
    const priceInCents = parseInt(this.dataset.customColorPrice || '0');
    const price = priceInCents / 100; // Convert from cents to dollars

    return { colorCode, price };
  }

  /**
   * Gets warranty option data if provided.
   *
   * @returns {{price: number} | null} Warranty data or null
   */
  #getWarrantyData() {
    const warrantyCheckbox = /** @type {HTMLInputElement | null} */ (document.getElementById('warranty-checkbox'));
    if (!warrantyCheckbox || !warrantyCheckbox.checked) {
      return null;
    }

    // Read from the warranty-option dataset which is already in cents (Shopify prices are in cents)
    const warrantyOption = /** @type {HTMLElement | null} */ (document.querySelector('.warranty-option'));
    const priceInCents = parseFloat(warrantyOption?.dataset.warrantyPrice || '0');

    return { price: priceInCents };
  }

  /**
   * Determines how many unique base variants are present in the submission.
   * Currently this is equivalent to checking whether the main variant input has a value.
   *
   * @returns {number}
   */
  #getUniqueVariantCount() {
    const mainVariantId = this.refs.variantId?.value;
    return mainVariantId ? 1 : 0;
  }

  /**
   * Checks if a variant already exists in the cart.
   * @param {string} variantId - The variant ID to check
   * @returns {Promise<boolean>}
   */
  async #isVariantInCart(variantId) {
    try {
      const cart = await this.#getCurrentCart();

      if (!cart || !cart.items) {
        return false;
      }

      const result = cart.items.some(
        (/** @type {{ variant_id: string | number }} */ item) => String(item.variant_id) === String(variantId),
      );

      return result;
    } catch (error) {
      return false;
    }
  }

  /**
   * Checks if a variant exists in the cart with the same custom color code.
   * @param {string} variantId - The variant ID to check
   * @param {string} colorCode - The custom color code to check
   * @returns {Promise<boolean>}
   */
  async #isVariantInCartWithColorCode(variantId, colorCode) {
    if (!colorCode) return false;

    try {
      const cart = await this.#getCurrentCart();

      if (!cart || !cart.items) {
        return false;
      }

      const result = cart.items.some(
        (/** @type {{ variant_id: string | number; properties?: Record<string, string> }} */ item) => {
          if (String(item.variant_id) !== String(variantId)) return false;
          const paintColorCode = item.properties?.['Paint Color Code'];
          return paintColorCode && paintColorCode.trim() === colorCode.trim();
        },
      );

      return result;
    } catch (error) {
      return false;
    }
  }

  /**
   * Fetches the current cart with shared caching and request deduplication.
   * @returns {Promise<{ items: Array<any> } | null>}
   */
  async #getCurrentCart() {
    const now = Date.now();

    // Return cached cart if still valid
    if (
      ProductFormComponent.#cartCache &&
      now - ProductFormComponent.#cartCache.timestamp < ProductFormComponent.#CART_CACHE_TTL
    ) {
      return ProductFormComponent.#cartCache.cart;
    }

    // If there's already a pending request, wait for it
    if (ProductFormComponent.#pendingCartRequest) {
      try {
        const cart = await ProductFormComponent.#pendingCartRequest;
        return cart;
      } catch {
        // If the pending request failed, continue to make a new one
      }
    }

    // Create a new request and share it
    ProductFormComponent.#pendingCartRequest = (async () => {
      try {
        const response = await fetch(Theme.routes.cart_url + '.js');
        if (!response.ok) {
          ProductFormComponent.#pendingCartRequest = null;
          return null;
        }
        const cart = await response.json();

        // Update cache
        ProductFormComponent.#cartCache = {
          cart,
          timestamp: Date.now(),
        };

        ProductFormComponent.#pendingCartRequest = null;
        return cart || null;
      } catch (error) {
        ProductFormComponent.#pendingCartRequest = null;
        return null;
      }
    })();

    return ProductFormComponent.#pendingCartRequest;
  }

  /**
   * Gets the warranty variant ID from the SKU map.
   * @returns {string | null}
   */
  #getWarrantyVariantIdFromSku() {
    const warrantyOption = /** @type {HTMLElement | null} */ (this.querySelector('.warranty-option'));
    const sku = warrantyOption?.dataset.warrantySku || this.dataset.warrantySku || '';

    if (!sku) return null;

    const skuMap = this.#getSkuMapFromScript('data-warranty-sku-map');
    if (!skuMap) return null;

    const variantId = skuMap[sku] || null;
    return variantId ? String(variantId) : null;
  }

  /**
   * Counts unique base variants in the cart (excluding warranty and custom color add-ons).
   * @param {Array<any>} cartItems - Cart items array
   * @returns {number}
   */
  #countUniqueBaseVariants(cartItems) {
    if (!cartItems || !Array.isArray(cartItems)) return 0;

    const warrantyVariantId = this.#getWarrantyVariantIdFromSku();
    const customColorVariantId = this.#getCustomColorVariantId();
    const uniqueVariants = new Set();

    cartItems.forEach((/** @type {{ variant_id: string | number; key?: string }} */ item) => {
      const variantId = String(item.variant_id);
      // Skip warranty and custom color add-ons
      if (warrantyVariantId && variantId === warrantyVariantId) return;
      if (customColorVariantId && variantId === customColorVariantId) return;
      uniqueVariants.add(variantId);
    });

    return uniqueVariants.size;
  }

  /**
   * Gets warranty line item from cart.
   * @param {Array<any>} cartItems - Cart items array
   * @returns {{ id: string; quantity: number } | null}
   */
  #getWarrantyLineItem(cartItems) {
    if (!cartItems || !Array.isArray(cartItems)) return null;

    const warrantyVariantId = this.#getWarrantyVariantIdFromSku();
    if (!warrantyVariantId) return null;

    const warrantyItem = cartItems.find(
      (/** @type {{ variant_id: string | number }} */ item) => String(item.variant_id) === warrantyVariantId,
    );

    if (!warrantyItem) return null;

    return {
      id: String(warrantyItem.key || warrantyItem.id),
      quantity: warrantyItem.quantity || 0,
    };
  }

  /**
   * Syncs warranty quantity to match the number of unique base variants in the cart.
   * Note: Never removes warranty from cart, only adds when needed.
   */
  async #syncWarrantyQuantity() {
    // Do nothing - warranty is never removed from cart
    // This function is kept to maintain the cart monitor callback structure
  }

  #startCartMonitor() {
    // Use shared cart monitor to prevent multiple intervals
    if (!ProductFormComponent.#sharedCartMonitorInterval) {
      ProductFormComponent.#sharedCartMonitorInterval = setInterval(async () => {
        try {
          const cart = await ProductFormComponent.#getCurrentCartShared();
          if (!cart || !cart.items) return;

          // Notify all registered callbacks
          ProductFormComponent.#cartMonitorCallbacks.forEach((callback) => {
            try {
              callback(cart);
            } catch (error) {
              // Ignore errors in callbacks
            }
          });
        } catch (error) {
          // Ignore errors
        }
      }, 2000); // Check every 2 seconds instead of 1 to reduce load
    }

    // Register this component's callback
    const checkCartChanges = (
      /** @type {{ items?: Array<{ id: string | number; quantity: number }> } | null} */ cart,
    ) => {
      if (!cart || !cart.items) return;

      const cartHash = JSON.stringify(
        cart.items.map((/** @type {{ id: string | number; quantity: number }} */ item) => ({
          id: item.id,
          quantity: item.quantity,
        })),
      );
      if (cartHash !== this.#lastCartHash) {
        this.#lastCartHash = cartHash;
        setTimeout(() => {
          void this.#syncWarrantyQuantity();
        }, 500);
      }
    };

    this.#cartMonitorCallback = checkCartChanges;
    ProductFormComponent.#cartMonitorCallbacks.add(checkCartChanges);
  }

  /**
   * Static method to get current cart (used by shared monitor).
   * @returns {Promise<{ items: Array<any> } | null>}
   */
  static async #getCurrentCartShared() {
    const now = Date.now();

    // Return cached cart if still valid
    if (
      ProductFormComponent.#cartCache &&
      now - ProductFormComponent.#cartCache.timestamp < ProductFormComponent.#CART_CACHE_TTL
    ) {
      return ProductFormComponent.#cartCache.cart;
    }

    // If there's already a pending request, wait for it
    if (ProductFormComponent.#pendingCartRequest) {
      try {
        return await ProductFormComponent.#pendingCartRequest;
      } catch {
        // If the pending request failed, continue to make a new one
      }
    }

    // Create a new request and share it
    ProductFormComponent.#pendingCartRequest = (async () => {
      try {
        const response = await fetch(Theme.routes.cart_url + '.js');
        if (!response.ok) {
          ProductFormComponent.#pendingCartRequest = null;
          return null;
        }
        const cart = await response.json();

        // Update cache
        ProductFormComponent.#cartCache = {
          cart,
          timestamp: Date.now(),
        };

        ProductFormComponent.#pendingCartRequest = null;
        return cart || null;
      } catch (error) {
        ProductFormComponent.#pendingCartRequest = null;
        return null;
      }
    })();

    return ProductFormComponent.#pendingCartRequest;
  }

  /**
   * Invalidates the cart cache (call this after cart updates).
   */
  static #invalidateCartCache() {
    ProductFormComponent.#cartCache = null;
    ProductFormComponent.#pendingCartRequest = null;
  }

  /**
   * Gets the SKU map from a script tag with the given data attribute.
   * @param {string} dataAttribute - The data attribute name to search for
   * @returns {Record<string, string> | null}
   */
  #getSkuMapFromScript(dataAttribute) {
    // Search in the closest section or document since script tags are outside the component
    const searchRoot = this.closest('.shopify-section') || document;
    const scriptTag = searchRoot.querySelector(`script[type="application/json"][${dataAttribute}]`);
    if (!scriptTag || !scriptTag.textContent) return null;

    try {
      return JSON.parse(scriptTag.textContent.trim());
    } catch (e) {
      console.warn(`Failed to parse SKU map from ${dataAttribute}:`, e);
      return null;
    }
  }

  /**
   * Resolves the hidden custom color variant ID by looking up the SKU in the SKU map.
   * @returns {string | null}
   */
  #getCustomColorVariantId() {
    const customColorData = this.#getCustomColorData();
    if (!customColorData) return null;

    // Get the SKU from the custom color option element
    const customColorOption = /** @type {HTMLElement | null} */ (this.querySelector('.custom-color-option'));
    const sku = customColorOption?.dataset.hiddenVariantSku || this.dataset.customColorSku || '';

    if (!sku) return null;

    // Get the SKU map from the script tag
    const skuMap = this.#getSkuMapFromScript('data-custom-color-sku-map');
    if (!skuMap) return null;

    // Look up the variant ID by SKU
    const variantId = skuMap[sku] || null;
    return variantId ? String(variantId) : null;
  }

  /**
   * Resolves the hidden warranty variant ID by looking up the SKU in the SKU map.
   * @returns {string | null}
   */
  #getWarrantyVariantId() {
    const warrantyData = this.#getWarrantyData();
    if (!warrantyData) return null;

    // Get the SKU from the warranty option element or component dataset
    const warrantyOption = /** @type {HTMLElement | null} */ (this.querySelector('.warranty-option'));
    const sku = warrantyOption?.dataset.warrantySku || this.dataset.warrantySku || '';

    if (!sku) return null;

    // Get the SKU map from the script tag
    const skuMap = this.#getSkuMapFromScript('data-warranty-sku-map');
    if (!skuMap) return null;

    // Look up the variant ID by SKU
    const variantId = skuMap[sku] || null;
    return variantId ? String(variantId) : null;
  }

  /**
   * No longer needed - custom color fee is handled as line item properties on the main product
   * @deprecated This method is no longer used
   */
  async #addCustomColorFeeToCart(/** @type {any} */ customColorData) {
    // This method is deprecated - custom color fee is now handled as line item properties
    // on the main product instead of adding a separate hidden product
  }

  /**
   * Checks if any inputs with aria-disabled="true" are checked.
   *
   * @returns {boolean} True if any disabled inputs are checked
   */
  #hasDisabledInputsChecked() {
    const disabledInputs = this.querySelectorAll('input[aria-disabled="true"]:checked');
    return disabledInputs.length > 0;
  }

  /**
   * Updates the add to cart button state based on variant availability and disabled inputs.
   */
  #updateAddToCartButtonState() {
    const hasDisabledInputsChecked = this.#hasDisabledInputsChecked();
    const addToCartButtonContainer = this.refs.addToCartButtonContainer;
    if (addToCartButtonContainer && typeof addToCartButtonContainer.enable === 'function') {
      addToCartButtonContainer.enable();
    }
    if (hasDisabledInputsChecked) {
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if any disabled inputs are checked and prevent submission
    if (this.#hasDisabledInputsChecked()) {
      return;
    }

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs?.addToCartButton?.getAttribute('disabled') === 'true') {
      return;
    }

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    // Handle add-on products
    const addOnVariants = this.#getSelectedAddOnVariants();
    let nextItemIndex = 1;
    const addOnQuantity = '1';
    addOnVariants.forEach(({ id, sku }) => {
      if (!id) return;
      formData.append(`items[${nextItemIndex}][id]`, id);
      formData.append(`items[${nextItemIndex}][quantity]`, addOnQuantity);
      if (sku) formData.append(`items[${nextItemIndex}][properties][SKU]`, sku);
      nextItemIndex += 1;
    });

    const uniqueVariantCount = Math.max(this.#getUniqueVariantCount(), 0);
    const mainQuantity = Math.max(
      parseInt(String(formData.get('quantity') || this.dataset.quantityDefault || '1'), 10) || 1,
      1,
    );

    // Handle custom color option
    const customColorData = this.#getCustomColorData();
    if (customColorData) {
      // Always add the Custom Color Option product. Shopify will merge line items
      // with the same variant ID + same properties, so quantity increments correctly
      // when the same product+color is added again.
      const customColorVariantId = this.#getCustomColorVariantId();
      if (customColorVariantId && uniqueVariantCount > 0) {
        formData.append(`items[${nextItemIndex}][id]`, customColorVariantId);
        formData.append(`items[${nextItemIndex}][quantity]`, mainQuantity.toString());
        const customColorSku = /** @type {HTMLElement | null} */ (
          this.querySelector('.custom-color-option')?.dataset.hiddenVariantSku || this.dataset.customColorSku || ''
        ).trim();
        if (customColorSku) formData.append(`items[${nextItemIndex}][properties][SKU]`, customColorSku);
        // Add Paint Color Code and Associated Product (main product SKU only) so CCO is associated by SKU
        formData.append(`items[${nextItemIndex}][properties][Paint Color Code]`, customColorData.colorCode);
        const mainVariantSku = (this.dataset.currentVariantSku || this.dataset.variantSku || '').trim();
        if (mainVariantSku) formData.append(`items[${nextItemIndex}][properties][Associated Product]`, mainVariantSku);
        nextItemIndex += 1;
      }

      // Always add properties to the main product, even if variant already exists
      formData.append('properties[Paint Color Code]', customColorData.colorCode);
      formData.append('properties[Non-Refundable Custom Color Fee]', `$${customColorData.price.toFixed(2)}`);
    }

    // Handle warranty option
    const warrantyData = this.#getWarrantyData();
    if (warrantyData) {
      // Always add the warranty product when selected. Shopify creates separate line items when
      // properties differ, so an associated warranty (with Associated Product) is a different line
      // from a standalone warranty already in cart.
      const warrantyVariantId = this.#getWarrantyVariantId();
      if (warrantyVariantId && uniqueVariantCount > 0) {
        formData.append(`items[${nextItemIndex}][id]`, warrantyVariantId);
        formData.append(`items[${nextItemIndex}][quantity]`, mainQuantity.toString());
        const warrantySku = /** @type {HTMLElement | null} */ (
          this.querySelector('.warranty-option')?.dataset.warrantySku || this.dataset.warrantySku || ''
        ).trim();
        if (warrantySku) formData.append(`items[${nextItemIndex}][properties][SKU]`, warrantySku);
        const mainVariantSkuForWarranty = (this.dataset.currentVariantSku || this.dataset.variantSku || '').trim();
        if (mainVariantSkuForWarranty) {
          formData.append(`items[${nextItemIndex}][properties][Associated Product]`, mainVariantSkuForWarranty);
        }
        nextItemIndex += 1;
      }

      // Always add properties to the main product, even if variant already exists
      formData.append('properties[This product has opted for warranty]', 'Yes');
    }

    const sku = (this.dataset.currentVariantSku || this.dataset.variantSku || '').trim();
    if (sku) {
      formData.append('properties[SKU]', sku);
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.text())
      .then((text) => {
        let response;
        try {
          response = JSON.parse(text);
        } catch {
          const message = 'Something went wrong. Please try again.';
          this.dispatchEvent(new CartErrorEvent(form.getAttribute('id') || '', message, {}, {}));

          if (addToCartTextError) {
            addToCartTextError.classList.remove('hidden');
            const textNode = addToCartTextError.childNodes[2];
            if (textNode) {
              textNode.textContent = message;
            } else {
              addToCartTextError.appendChild(document.createTextNode(message));
            }
          }

          this.#setLiveRegionText(message);

          this.#timeout = setTimeout(() => {
            if (addToCartTextError) addToCartTextError.classList.add('hidden');
            this.#clearLiveRegionText();
          }, 10000);

          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            }),
          );
          return;
        }

        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors),
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            }),
          );

          return;
        }

        const id = formData.get('id');

        if (addToCartTextError) {
          addToCartTextError.classList.add('hidden');
          addToCartTextError.removeAttribute('aria-live');
        }

        if (!id) throw new Error('Form ID is required');

        // Add aria-live region to inform screen readers that the item was added
        if (this.refs.addToCartButtonContainer?.refs?.addToCartButton) {
          const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
          const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
          const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

          this.#setLiveRegionText(addedText);

          setTimeout(() => {
            this.#clearLiveRegionText();
          }, 5000);
        }

        this.dispatchEvent(
          new CartAddEvent({}, id.toString(), {
            source: 'product-form-component',
            itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
            productId: this.dataset.productId,
            sections: response.sections,
          }),
        );

        // Clear custom color input after successful add to cart
        this.#clearCustomColorInput();

        // Clear warranty option after successful add to cart
        this.#clearWarrantyOption();

        // Clear selected addons after successful add to cart
        this.#clearSelectedAddons();

        // Custom color fee is handled as line item properties on the main product

        // Invalidate cart cache after successful add to cart
        ProductFormComponent.#invalidateCartCache();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        try {
          cartPerformance.measureFromEvent('add:user-action', event);
        } catch (e) {
          // Skip performance measurement if it fails
        }
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * Clear the custom color input field and reset quantity after successful add to cart
   */
  #clearCustomColorInput() {
    const colorInput = /** @type {HTMLInputElement | null} */ (document.getElementById('custom-color-code'));
    if (colorInput) {
      colorInput.value = '';
    }

    // Close the custom color option
    const colorHeader = document.querySelector('.custom-color-option__header');
    const colorContent = document.querySelector('.custom-color-option__content');
    if (colorHeader) {
      colorHeader.classList.remove('active');
    }
    if (colorContent) {
      colorContent.classList.remove('open');
    }

    // Reset quantity to default minimum
    this.#resetQuantity();

    // Revert the add to cart button price to original variant price
    this.updateAddToCartButtonPrice();
  }

  /**
   * Clear the warranty option after successful add to cart
   */
  #clearWarrantyOption() {
    const warrantyCheckbox = /** @type {HTMLInputElement | null} */ (document.getElementById('warranty-checkbox'));
    const warrantyOption = document.querySelector('.warranty-option');
    const descriptionContent = document.querySelector('.warranty-option__description-content');

    if (warrantyCheckbox) {
      warrantyCheckbox.checked = false;
    }

    if (warrantyOption) {
      warrantyOption.classList.remove('selected');
    }

    if (descriptionContent) {
      descriptionContent.classList.remove('open');
    }

    // Reset quantity to default minimum
    this.#resetQuantity();

    // Revert the add to cart button price to original variant price
    this.updateAddToCartButtonPrice();
  }

  /**
   * Clear selected addon cards after successful add to cart
   */
  #clearSelectedAddons() {
    const selectedAddonCards = document.querySelectorAll('.related-products-dropdown .variant-card.selected');
    selectedAddonCards.forEach((card) => {
      card.classList.remove('selected');
    });
  }

  /**
   * Reset quantity to the default minimum value
   */
  #resetQuantity() {
    const quantityInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="quantity"]'));
    if (quantityInput) {
      const defaultQuantity = this.dataset.quantityDefault || '1';
      quantityInput.value = defaultQuantity;
    }
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    const newVariantId = event.detail.resource?.id ?? '';
    variantId.value = newVariantId;

    if (event.detail.resource) {
      const variant = /** @type {any} */ (event.detail.resource);
      const variantPrice =
        variant.price || /** @type {any} */ (event.detail.data)?.variant?.price || this.dataset.variantPrice || '0';
      this.dataset.variantPrice = variantPrice;

      const productVariantMedia = variant.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');

      if (variant.sku) {
        this.#updateProductSku(variant.sku);
      }
    }

    if (newVariantId) {
      let metafieldsScript = event.detail.data.html?.querySelector(
        `script[type="application/json"][data-variant-metafields][data-variant-id="${newVariantId}"]`,
      );

      if (!metafieldsScript && event.detail.data.html) {
        const buyButtonsBlock = event.detail.data.html.querySelector('.buy-buttons-block');
        if (buyButtonsBlock) {
          metafieldsScript = buyButtonsBlock.querySelector(
            `script[type="application/json"][data-variant-metafields][data-variant-id="${newVariantId}"]`,
          );
        }
      }

      if (metafieldsScript) {
        try {
          const metafieldsData = JSON.parse(metafieldsScript.textContent);

          const stockStatusRaw = String(metafieldsData.stockStatus || '');
          const stockStatus = stockStatusRaw.toLowerCase().replace(/\s+/g, '');

          const preorderRaw = metafieldsData.preorder;
          const preorderString = String(preorderRaw).toLowerCase();
          const isPreorderEnabled =
            preorderRaw === true || preorderString === 'yes' || preorderString === 'true' || preorderString === '1';

          const backorderRaw = metafieldsData.backorder;
          const backorderString = String(backorderRaw || '').toLowerCase();
          const isBackorderEnabled =
            backorderRaw === true || backorderString === 'yes' || backorderString === 'true' || backorderString === '1';

          const detailMessage = String(metafieldsData.statusMessageDetail || metafieldsData.backOrderMessage || '');

          this.#updateFromMetafields({
            stockStatus,
            preorderEnabled: isPreorderEnabled ? 'true' : 'false',
            backorderEnabled: isBackorderEnabled ? 'true' : 'false',
            stockStatusMessage: detailMessage,
            backorderMessage: '',
            preorderMessage: '',
            stockStatusType: '',
          });
        } catch (e) {
          this.#fetchAndUpdateVariantMetafields(newVariantId);
        }
      } else {
        this.#fetchAndUpdateVariantMetafields(newVariantId);
      }
    }

    const variantPicker = this.closest('.shopify-section')?.querySelector('variant-picker');
    if (variantPicker) {
      const scriptTag = variantPicker.querySelector('script[type="application/json"]');
      if (scriptTag) {
        try {
          const variantData = JSON.parse(scriptTag.textContent);
          if (variantData.price) {
            this.dataset.variantPrice = variantData.price.toString();
          }
          if (variantData.sku && !(/** @type {any} */ (event.detail.resource)?.sku)) {
            this.#updateProductSku(variantData.sku);
          }
        } catch (e) {}
      }
    }

    this.updateAddToCartButtonPrice();

    // Auto-select next available option if current selection became disabled
    // This ensures we always have a valid variant selection and can add to cart
    this.#autoSelectNextAvailableOption(event);
  };

  /**
   * Automatically selects the next available option if the currently selected option
   * has become disabled after a variant update.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #autoSelectNextAvailableOption(event) {
    const variantPicker = this.closest('.shopify-section')?.querySelector('variant-picker');
    if (!variantPicker) return;

    // Use requestAnimationFrame to ensure DOM has been fully updated after morph
    requestAnimationFrame(() => {
      // Get all option groups (fieldsets for radio buttons, selects for dropdowns)
      const optionGroups = variantPicker.querySelectorAll('fieldset, select.variant-option__select');

      let hasAutoSelected = false;

      for (const group of optionGroups) {
        if (group instanceof HTMLFieldSetElement) {
          // Handle radio button groups
          const checkedInput = /** @type {HTMLInputElement | null} */ (
            group.querySelector('input[type="radio"]:checked')
          );
          if (checkedInput && checkedInput.getAttribute('aria-disabled') === 'true') {
            // Find the first available option in this group
            const availableInput = /** @type {HTMLInputElement | null} */ (
              group.querySelector('input[type="radio"]:not([aria-disabled="true"])')
            );
            if (availableInput) {
              availableInput.checked = true;
              checkedInput.checked = false;
              hasAutoSelected = true;
              // Trigger change event to update variant
              availableInput.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        } else if (group instanceof HTMLSelectElement) {
          // Handle select dropdowns
          const selectedOption = group.options[group.selectedIndex];
          if (selectedOption) {
            // Check if the selected option text indicates it's unavailable
            const optionText = selectedOption.textContent.trim();
            const isUnavailable =
              optionText.includes('unavailable') ||
              (optionText.includes(' - ') && optionText.toLowerCase().includes('unavailable'));

            if (isUnavailable) {
              // Find the first available option (one that doesn't have "unavailable" in its text)
              const availableOption = Array.from(group.options).find((opt) => {
                const text = opt.textContent.trim();
                return (
                  !text.includes('unavailable') && !(text.includes(' - ') && text.toLowerCase().includes('unavailable'))
                );
              });
              if (availableOption) {
                group.value = availableOption.value;
                hasAutoSelected = true;
                // Trigger change event to update variant
                group.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }
          }
        }
      }

      // If we auto-selected an option, the change event will trigger another variant update
      // which will handle the rest of the logic
    });
  }

  /**
   * Reads variant metafields from the current DOM (for initial page load).
   * @param {string | number} variantId - The variant ID to read metafields for
   */
  #readMetafieldsFromDOM(variantId) {
    let metafieldsScript = document.querySelector(
      `script[type="application/json"][data-variant-metafields][data-variant-id="${variantId}"]`,
    );

    if (!metafieldsScript) {
      const buyButtonsBlock = this.closest('.buy-buttons-block') || document.querySelector('.buy-buttons-block');
      if (buyButtonsBlock) {
        metafieldsScript = buyButtonsBlock.querySelector(
          `script[type="application/json"][data-variant-metafields][data-variant-id="${variantId}"]`,
        );
      }
    }

    if (metafieldsScript) {
      try {
        const metafieldsData = JSON.parse(metafieldsScript.textContent);

        const stockStatusRaw = String(metafieldsData.stockStatus || '');
        const stockStatus = stockStatusRaw.toLowerCase().replace(/\s+/g, '');

        const preorderRaw = metafieldsData.preorder;
        const preorderString = String(preorderRaw).toLowerCase();
        const isPreorderEnabled =
          preorderRaw === true || preorderString === 'yes' || preorderString === 'true' || preorderString === '1';

        const backorderRaw = metafieldsData.backorder;
        const backorderString = String(backorderRaw || '').toLowerCase();
        const isBackorderEnabled =
          backorderRaw === true || backorderString === 'yes' || backorderString === 'true' || backorderString === '1';

        const detailMessage = String(metafieldsData.statusMessageDetail || metafieldsData.backOrderMessage || '');

        this.#updateFromMetafields({
          stockStatus,
          preorderEnabled: isPreorderEnabled ? 'true' : 'false',
          backorderEnabled: isBackorderEnabled ? 'true' : 'false',
          stockStatusMessage: detailMessage,
          backorderMessage: '',
          preorderMessage: '',
          stockStatusType: '',
        });
        return;
      } catch (e) {}
    } else {
      const variantPicker = this.closest('.shopify-section')?.querySelector('variant-picker');
      if (variantPicker) {
        const scriptTag = variantPicker.querySelector('script[type="application/json"]');
        if (scriptTag) {
          try {
            const variantData = JSON.parse(scriptTag.textContent);
            const isAvailable = variantData.available !== false;
            this.#variantAvailable = isAvailable;
            this.#updateAddToCartButtonState();
            return;
          } catch (e) {}
        }
      }
      this.#variantAvailable = true;
      this.#updateAddToCartButtonState();
    }
  }

  /**
   * Fetches variant metafields via section rendering API and updates the UI.
   * @param {string | number} variantId - The variant ID to fetch metafields for
   */
  async #fetchAndUpdateVariantMetafields(variantId) {
    try {
      const productUrl = this.dataset.productUrl;
      if (!productUrl) return;

      // Build URL to re-render the buy-buttons block with the selected variant
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', variantId.toString());
      url.searchParams.set('section_id', this.closest('.shopify-section')?.id || '');

      const response = await fetch(url.toString());
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const buyButtonsBlock = doc.querySelector('.buy-buttons-block');

      const metafieldsScript = doc.querySelector(
        `script[type="application/json"][data-variant-metafields][data-variant-id="${variantId}"]`,
      );

      if (metafieldsScript) {
        try {
          const metafieldsData = JSON.parse(metafieldsScript.textContent);

          const stockStatusRaw = String(metafieldsData.stockStatus || '');
          const stockStatus = stockStatusRaw.toLowerCase().replace(/\s+/g, '');

          const preorderRaw = metafieldsData.preorder;
          const preorderString = String(preorderRaw).toLowerCase();
          const isPreorderEnabled =
            preorderRaw === true || preorderString === 'yes' || preorderString === 'true' || preorderString === '1';

          const backorderRaw = metafieldsData.backorder;
          const backorderString = String(backorderRaw || '').toLowerCase();
          const isBackorderEnabled =
            backorderRaw === true || backorderString === 'yes' || backorderString === 'true' || backorderString === '1';

          const detailMessage = String(metafieldsData.statusMessageDetail || metafieldsData.backOrderMessage || '');

          this.#updateFromMetafields({
            stockStatus,
            preorderEnabled: isPreorderEnabled ? 'true' : 'false',
            backorderEnabled: isBackorderEnabled ? 'true' : 'false',
            stockStatusMessage: detailMessage,
            backorderMessage: '',
            preorderMessage: '',
            stockStatusType: '',
          });
          return;
        } catch (e) {}
      }

      const variantPicker = doc.querySelector('variant-picker script[type="application/json"]');
      if (variantPicker) {
        try {
          const variantData = JSON.parse(variantPicker.textContent);
          const isAvailable = variantData.available !== false;
          this.#variantAvailable = isAvailable;
          this.#updateAddToCartButtonState();
          return;
        } catch (e) {}
      }

      this.#variantAvailable = true;
      this.#updateAddToCartButtonState();
    } catch (error) {
      this.#variantAvailable = true;
      this.#updateAddToCartButtonState();
    }
  }

  /**
   * Processes variant data to extract metafields and update UI.
   * @param {any} variantData - The variant data object
   * @param {string | number} variantId - The variant ID
   */
  async #processVariantMetafields(variantData, variantId) {
    if (variantData.metafields?.custom) {
      const metafields = variantData.metafields.custom;
      const stockStatus = metafields.stock_status?.value || metafields.stock_status || '';
      const preorder = metafields.preorder?.value || metafields.preorder || '';
      const backOrderMessage = metafields.back_order_message?.value || metafields.back_order_message || '';
      const stockStatusNormalized = String(stockStatus).toLowerCase().replace(/\s+/g, '');

      const preorderString = String(preorder).toLowerCase();
      const isPreorderEnabled =
        preorder === true || preorderString === 'yes' || preorderString === 'true' || preorderString === '1';

      this.#updateFromMetafields({
        stockStatus: stockStatusNormalized,
        preorderEnabled: isPreorderEnabled ? 'true' : 'false',
        backorderEnabled: 'false',
        stockStatusMessage: String(backOrderMessage || ''),
        backorderMessage: '',
        preorderMessage: '',
        stockStatusType: '',
      });
    } else {
      await this.#fetchVariantMetafieldsViaAPI(variantId);
    }
  }

  /**
   * Fetches variant metafields via Shopify Storefront API.
   * @param {string | number} variantId - The variant ID
   */
  async #fetchVariantMetafieldsViaAPI(variantId) {
    try {
      const productUrl = this.dataset.productUrl;
      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', variantId.toString());
      url.searchParams.set('section_id', this.closest('.shopify-section')?.id || '');

      const response = await fetch(url.toString());
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');

      const metafieldsScript = doc.querySelector(
        `script[type="application/json"][data-variant-metafields][data-variant-id="${variantId}"]`,
      );
      if (metafieldsScript) {
        try {
          const metafieldsData = JSON.parse(metafieldsScript.textContent);
          const stockStatusRaw = String(metafieldsData.stockStatus || '');
          const stockStatus = stockStatusRaw.toLowerCase().replace(/\s+/g, '');

          const preorderRaw = metafieldsData.preorder;
          const preorderString = String(preorderRaw).toLowerCase();
          const isPreorderEnabled =
            preorderRaw === true || preorderString === 'yes' || preorderString === 'true' || preorderString === '1';

          const backorderRaw = metafieldsData.backorder;
          const backorderString = String(backorderRaw || '').toLowerCase();
          const isBackorderEnabled =
            backorderRaw === true || backorderString === 'yes' || backorderString === 'true' || backorderString === '1';

          const detailMessage = String(metafieldsData.statusMessageDetail || metafieldsData.backOrderMessage || '');

          this.#updateFromMetafields({
            stockStatus,
            preorderEnabled: isPreorderEnabled ? 'true' : 'false',
            backorderEnabled: isBackorderEnabled ? 'true' : 'false',
            stockStatusMessage: detailMessage,
            backorderMessage: '',
            preorderMessage: '',
            stockStatusType: '',
          });
          return;
        } catch (e) {}
      }

      const variantPicker = doc.querySelector('variant-picker script[type="application/json"]');
      if (variantPicker) {
        const variantData = JSON.parse(variantPicker.textContent);
        const isAvailable = variantData.available !== false;
        this.#variantAvailable = isAvailable;
        this.#updateAddToCartButtonState();
      }
    } catch (error) {
      this.#variantAvailable = true;
      this.#updateAddToCartButtonState();
    }
  }

  /**
   * Updates UI based on variant metafields.
   * @param {Object} metafields - The metafields data
   * @param {string} metafields.stockStatus - Stock status value
   * @param {string} metafields.preorderEnabled - Whether preorder is enabled
   * @param {string} metafields.backorderEnabled - Whether backorder is enabled
   * @param {string} metafields.stockStatusMessage - Stock status message
   * @param {string} metafields.backorderMessage - Backorder message from theme settings
   * @param {string} metafields.preorderMessage - Preorder message from theme settings
   * @param {string} metafields.stockStatusType - Stock status type
   */
  #updateFromMetafields({
    stockStatus,
    preorderEnabled,
    backorderEnabled,
    stockStatusMessage,
    backorderMessage,
    preorderMessage,
    stockStatusType,
  }) {
    this.dataset.stockStatus = stockStatus || '';
    this.dataset.preorderEnabled = preorderEnabled || 'false';
    this.dataset.backorderEnabled = backorderEnabled || 'false';
    this.dataset.stockStatusMessage = stockStatusMessage || '';
    this.dataset.stockStatusType = stockStatusType || '';

    const normalizedStockStatus = (stockStatus || '').toLowerCase();
    const isPreorderEnabled = (preorderEnabled || '').toLowerCase() === 'true';
    const isBackorderEnabled = (backorderEnabled || '').toLowerCase() === 'true';

    let canPurchase = true;
    let message = '';
    let type = '';

    const isInStock = normalizedStockStatus === 'instock' || normalizedStockStatus === 'in stock';
    const shouldHideMessage = isInStock && !isPreorderEnabled && !isBackorderEnabled;

    if (shouldHideMessage) {
      message = '';
      type = '';
    } else if (isPreorderEnabled) {
      type = 'preorder';
      message = this.#buildAvailabilityMessage('preorder', stockStatusMessage);
    } else if (isBackorderEnabled) {
      type = 'backorder';
      message = this.#buildAvailabilityMessage('backorder', stockStatusMessage);
    } else {
      type = 'backorder';
      message = this.#buildAvailabilityMessage('backorder', stockStatusMessage);
    }

    this.#variantAvailable = canPurchase;

    if (message && type) {
      this.#updateStockStatusMessage(message, type);
    } else {
      this.#updateStockStatusMessage('', '');
    }

    this.#updateAddToCartButtonState();

    if (canPurchase) {
      this.updateAddToCartButtonPrice();
    }
  }

  /**
   * Gets default preorder message from theme settings.
   * @returns {string}
   */
  #getDefaultPreorderMessage() {
    return typeof Theme !== 'undefined' && Theme.translations?.preorder_message
      ? Theme.translations.preorder_message
      : '';
  }

  /**
   * Gets default backorder message from theme settings.
   * @returns {string}
   */
  #getDefaultBackorderMessage() {
    return typeof Theme !== 'undefined' && Theme.translations?.backorder_message
      ? Theme.translations.backorder_message
      : '';
  }

  /**
   * Builds the availability message string.
   * @param {'preorder' | 'backorder'} type
   * @param {string} detailMessage
   * @returns {string}
   */
  #buildAvailabilityMessage(type, detailMessage) {
    const normalizedType = type === 'preorder' ? 'preorder' : 'backorder';
    const baseMessage = normalizedType === 'preorder' ? 'Available on Preorder' : 'Available on backorder';
    const trimmedDetail = (detailMessage || '').trim();

    return trimmedDetail ? `${baseMessage}, ${trimmedDetail}` : baseMessage;
  }

  /**
   * Updates the stock status message element based on variant selection.
   * @param {string | undefined} message - The stock status message
   * @param {string | undefined} type - The stock status type (backorder, preorder, etc.)
   */
  #updateStockStatusMessage(message, type) {
    const stockStatusMessageElement = this.refs.stockStatusMessage;
    if (!stockStatusMessageElement) return;

    const messageTextElement = stockStatusMessageElement.querySelector('.product-form-stock-message__text');

    if (message && message.trim() !== '' && type) {
      // Update message text
      if (messageTextElement) {
        messageTextElement.textContent = message;
      }

      // Update classes - remove all type classes and add the new one
      stockStatusMessageElement.classList.remove(
        'product-form-stock-message--backorder',
        'product-form-stock-message--preorder',
        'hidden',
      );
      stockStatusMessageElement.classList.add(`product-form-stock-message--${type}`);
    } else {
      stockStatusMessageElement.classList.add('hidden');
      stockStatusMessageElement.classList.remove(
        'product-form-stock-message--backorder',
        'product-form-stock-message--preorder',
      );
      if (messageTextElement) {
        messageTextElement.textContent = '';
      }
    }
  }

  /**
   * Initializes the SKU from the variant on page load.
   * This ensures the SKU is displayed correctly when a variant is specified in the URL.
   */
  #initializeSkuFromVariant() {
    const isOnProductPage = this.closest('.shopify-section') && !this.closest('product-card');
    if (!isOnProductPage) return;

    const urlParams = new URLSearchParams(window.location.search);
    const variantIdFromUrl = urlParams.get('variant');
    const currentVariantId = this.refs.variantId?.value || variantIdFromUrl;

    if (!currentVariantId) return;

    const variantPicker = this.closest('.shopify-section')?.querySelector('variant-picker');
    if (variantPicker) {
      const scriptTag = variantPicker.querySelector('script[type="application/json"]');
      if (scriptTag) {
        try {
          const variantData = JSON.parse(scriptTag.textContent);
          // Check if this is the variant we're looking for
          if (variantData.id && String(variantData.id) === String(currentVariantId) && variantData.sku) {
            this.#updateProductSku(variantData.sku);
            return;
          }
        } catch (e) {}
      }
    }
  }

  /**
   * Updates the product SKU element with the selected variant's SKU.
   * Also stores the SKU on the component for use as a line item property when adding to cart.
   * @param {string} sku - The variant SKU to display
   */
  #updateProductSku(sku) {
    const value = sku ? String(sku).trim() : '';
    this.dataset.currentVariantSku = value;
    const productSkuElement = document.querySelector('.product-sku');
    if (productSkuElement && value) {
      productSkuElement.textContent = `SKU: ${value}`;
    }
  }

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    const addToCartButtonContainer = this.refs.addToCartButtonContainer;
    if (addToCartButtonContainer && typeof addToCartButtonContainer.enable === 'function') {
      addToCartButtonContainer.enable();
    }
  };

  /**
   * Handles quantity selector update events.
   */
  #onQuantityUpdate = () => {
    this.updateAddToCartButtonPrice();
  };

  /**
   * Handles input changes to update add to cart button state.
   */
  #onInputChange = (/** @type {Event} */ event) => {
    this.#updateAddToCartButtonState();

    // Update price when quantity changes, warranty checkbox changes, or custom color input changes
    if (
      (event.target && /** @type {HTMLInputElement} */ (event.target).name === 'quantity') ||
      /** @type {HTMLInputElement} */ (event.target).id === 'warranty-checkbox' ||
      /** @type {HTMLInputElement} */ (event.target).id === 'custom-color-code'
    ) {
      this.updateAddToCartButtonPrice();
    }
  };

  /**
   * Initializes the variant price from the currently selected variant.
   */
  #initializeVariantPrice() {
    const variantPicker = this.closest('.shopify-section')?.querySelector('variant-picker');
    if (variantPicker) {
      const scriptTag = variantPicker.querySelector('script[type="application/json"]');
      if (scriptTag) {
        try {
          const variantData = JSON.parse(scriptTag.textContent);
          if (variantData.price) {
            this.dataset.variantPrice = variantData.price.toString();
          }
        } catch (e) {}
      }
    }

    if (!this.dataset.variantPrice || this.dataset.variantPrice === '0') {
      const selectedVariantInput = this.closest('.shopify-section')?.querySelector('input[data-variant-id]:checked');
      if (selectedVariantInput) {
        const variantId = /** @type {HTMLElement} */ (selectedVariantInput).dataset.variantId;
        if (variantId) {
          const scriptTag = variantPicker?.querySelector('script[type="application/json"]');
          if (scriptTag) {
            try {
              const variantData = JSON.parse(scriptTag.textContent);
              if (variantData.id === variantId && variantData.price) {
                this.dataset.variantPrice = variantData.price.toString();
              }
            } catch (e) {}
          }
        }
      }
    }
  }

  /**
   * Updates the add to cart button price based on product type and variant.
   */
  updateAddToCartButtonPrice() {
    const addToCartButton = this.refs.addToCartButtonContainer?.refs?.addToCartButton;

    if (!addToCartButton) {
      return;
    }

    const variantPrice = parseInt(this.dataset.variantPrice || '0');
    const customColorPriceInCents = parseInt(this.dataset.customColorPrice || '0');

    const customColorInput = /** @type {HTMLInputElement | null} */ (document.getElementById('custom-color-code'));
    const hasCustomColorText = customColorInput && customColorInput.value.trim() !== '';

    const warrantyCheckbox = /** @type {HTMLInputElement | null} */ (document.getElementById('warranty-checkbox'));
    const hasWarrantySelected = warrantyCheckbox && warrantyCheckbox.checked;

    // Get warranty price from the warranty option element
    // Note: data-warranty-price is already in cents (Shopify prices are in cents)
    const warrantyOption = /** @type {HTMLElement | null} */ (document.querySelector('.warranty-option'));
    const warrantyPriceInCents = parseFloat(warrantyOption?.dataset.warrantyPrice || '0');

    // Get selected add-on prices
    const selectedAddOnCards = document.querySelectorAll('.variant-card.selected');
    let addOnTotalPrice = 0;
    selectedAddOnCards.forEach((card) => {
      const price = parseInt(/** @type {HTMLElement} */ (card).dataset.price || '0');
      addOnTotalPrice += price;
    });

    let quantityInput = /** @type {HTMLInputElement | null} */ (this.querySelector('input[name="quantity"]'));
    if (!quantityInput) {
      const quantitySelector = this.querySelector('quantity-selector-component');
      if (quantitySelector && quantitySelector.refs?.quantityInput) {
        quantityInput = quantitySelector.refs.quantityInput;
      }
    }
    const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
    const mainProductTotal = variantPrice * quantity;
    const customColorTotal = hasCustomColorText ? customColorPriceInCents : 0;
    const warrantyTotal = hasWarrantySelected ? warrantyPriceInCents : 0;
    const totalPrice = mainProductTotal + customColorTotal + warrantyTotal + addOnTotalPrice;

    const textContent = addToCartButton.querySelector('.add-to-cart-text__content');
    if (textContent) {
      const baseText = 'Add to Cart'; // Fixed text as requested
      const formattedPrice = this.#formatPrice(totalPrice);
      textContent.innerHTML = `<span class="add-to-cart-price">(${formattedPrice})</span> ${baseText}`;
    }
  }

  /**
   * Formats price from cents to currency string.
   * @param {number} priceInCents - Price in cents
   * @returns {string} Formatted price string
   */
  #formatPrice(priceInCents) {
    const dollars = priceInCents / 100;
    return dollars.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
